import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, onSnapshot, orderBy, limit, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const styles = {
    container: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: '20px'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px',
        flexWrap: 'wrap',
        gap: '16px'
    },
    title: {
        fontSize: '28px',
        fontWeight: '700',
        color: 'var(--text-primary)',
        letterSpacing: '-0.5px'
    },
    statsContainer: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '20px',
        marginBottom: '32px'
    },
    statCard: {
        background: 'var(--bg-primary)',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
        border: '1px solid var(--border-color)',
        display: 'flex',
        alignItems: 'center',
        gap: '20px'
    },
    statIcon: {
        width: '56px',
        height: '56px',
        borderRadius: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '24px'
    },
    statInfo: {
        flex: 1
    },
    statLabel: {
        fontSize: '13px',
        color: 'var(--text-muted)',
        fontWeight: '500',
        marginBottom: '6px',
        textTransform: 'uppercase',
        letterSpacing: '0.3px'
    },
    statValue: {
        fontSize: '26px',
        fontWeight: '700',
        color: 'var(--text-primary)'
    },
    tableContainer: {
        background: 'var(--bg-primary)',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
        border: '1px solid var(--border-color)'
    },
    tableTitle: {
        fontSize: '18px',
        fontWeight: '600',
        color: 'var(--text-primary)',
        marginBottom: '20px'
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse'
    },
    th: {
        textAlign: 'left',
        padding: '12px 16px',
        color: 'var(--text-muted)',
        fontSize: '13px',
        fontWeight: '600',
        borderBottom: '1px solid var(--border-color)',
        background: 'var(--bg-secondary)'
    },
    td: {
        padding: '16px',
        fontSize: '14px',
        color: 'var(--text-secondary)',
        borderBottom: '1px solid var(--border-color)'
    },
    statusBadge: {
        padding: '4px 8px',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: '600',
        display: 'inline-block'
    },
    button: {
        padding: '12px 24px',
        background: 'linear-gradient(135deg, #06C167, #8b5cf6)',
        color: 'white',
        border: 'none',
        borderRadius: '12px',
        cursor: 'pointer',
        fontWeight: '600',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        transition: 'all 0.2s',
        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)'
    }
};

export default function StoreDashboard() {
    const { userData, currentUser } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        todaySales: 0,
        weeklySales: 0,
        monthlySales: 0,
        totalProducts: 0,
        recentTransfersNum: 0,
        todaySaleCount: 0
    });
    const [recentSales, setRecentSales] = useState([]);
    const [recentTransfers, setRecentTransfers] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!userData || userData.role !== 'store' || !userData.companyId) {
            return;
        }

        const companyId = userData.companyId;
        const storeId = currentUser.uid;

        // Fetch Total Products from Inventory
        const inventoryUnsub = onSnapshot(collection(db, 'companies', companyId, 'stores', storeId, 'inventory'), (snap) => {
            let totalQty = 0;
            snap.docs.forEach(doc => {
                totalQty += doc.data().quantity || 0;
            });
            setStats(prev => ({ ...prev, totalProducts: totalQty }));
        });

        // Fetch Sales (today/weekly/monthly)
        const salesQuery = query(
            collection(db, 'companies', companyId, 'retailSales'),
            where('storeId', '==', storeId),
            orderBy('saleDate', 'desc')
        );

        const salesUnsub = onSnapshot(salesQuery, (snap) => {
            const now = new Date();
            const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const startOfWeek = new Date(now);
            startOfWeek.setDate(now.getDate() - now.getDay());
            startOfWeek.setHours(0, 0, 0, 0);
            const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

            let todayTotal = 0, weeklyTotal = 0, monthlyTotal = 0, todayCount = 0;
            const salesData = snap.docs.map(doc => {
                const data = doc.data();
                if (data.status === 'cancelled') return { id: doc.id, ...data };

                if (data.saleDate) {
                    const saleDate = data.saleDate.toDate();
                    if (saleDate >= startOfDay) {
                        todayTotal += data.total || 0;
                        todayCount++;
                    }
                    if (saleDate >= startOfWeek) weeklyTotal += data.total || 0;
                    if (saleDate >= startOfMonth) monthlyTotal += data.total || 0;
                }
                return { id: doc.id, ...data };
            });

            setStats(prev => ({ ...prev, todaySales: todayTotal, weeklySales: weeklyTotal, monthlySales: monthlyTotal, todaySaleCount: todayCount }));
            setRecentSales(salesData.filter(s => s.status !== 'cancelled').slice(0, 5));
        });

        // Fetch Recent Incoming Transfers
        const transfersQuery = query(
            collection(db, 'companies', companyId, 'storeTransfers'),
            where('to', '==', storeId),
            orderBy('date', 'desc'),
            limit(5)
        );

        const transfersUnsub = onSnapshot(transfersQuery, (snap) => {
            const transferData = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setRecentTransfers(transferData);
            setStats(prev => ({ ...prev, recentTransfersNum: transferData.length }));
            setLoading(false);
        });

        return () => {
            inventoryUnsub();
            salesUnsub();
            transfersUnsub();
        };
    }, [userData, currentUser]);

    if (loading) return <div style={styles.container}><p style={{color: 'var(--text-secondary)'}}>Yükleniyor...</p></div>;

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div>
                    <h1 style={styles.title}>Mağaza Özeti</h1>
                    <p style={{ color: 'var(--text-muted)', marginTop: '4px' }}>Hoş geldiniz, {userData?.storeName}</p>
                </div>
                <div>
                    <button style={styles.button} onClick={() => navigate('/magaza/satis')}>
                        🛒 Yeni Satış Yap
                    </button>
                </div>
            </div>

            <div style={styles.statsContainer}>
                <div style={styles.statCard}>
                    <div style={{ ...styles.statIcon, background: '#ecfdf5', color: '#10b981' }}>💵</div>
                    <div style={styles.statInfo}>
                        <div style={styles.statLabel}>Bugünkü Ciro</div>
                        <div style={styles.statValue}>₺{stats.todaySales.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>{stats.todaySaleCount} satış</div>
                    </div>
                </div>

                <div style={styles.statCard}>
                    <div style={{ ...styles.statIcon, background: '#eff6ff', color: '#3b82f6' }}>📊</div>
                    <div style={styles.statInfo}>
                        <div style={styles.statLabel}>Haftalık Ciro</div>
                        <div style={styles.statValue}>₺{stats.weeklySales.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</div>
                    </div>
                </div>

                <div style={styles.statCard}>
                    <div style={{ ...styles.statIcon, background: '#faf5ff', color: '#8b5cf6' }}>📈</div>
                    <div style={styles.statInfo}>
                        <div style={styles.statLabel}>Aylık Ciro</div>
                        <div style={styles.statValue}>₺{stats.monthlySales.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</div>
                    </div>
                </div>
                
                <div style={styles.statCard}>
                    <div style={{ ...styles.statIcon, background: '#fff7ed', color: '#f97316' }}>📦</div>
                    <div style={styles.statInfo}>
                        <div style={styles.statLabel}>Toplam Stok</div>
                        <div style={styles.statValue}>{stats.totalProducts}</div>
                    </div>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px' }}>
                <div style={styles.tableContainer}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <h2 style={styles.tableTitle}>Son Satışlar</h2>
                        <button onClick={() => navigate('/magaza/satislar')} style={{ background: 'none', border: 'none', color: '#06C167', cursor: 'pointer', fontWeight: '500' }}>Tümünü Gör</button>
                    </div>
                    {recentSales.length > 0 ? (
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Fiş No</th>
                                    <th style={styles.th}>Tarih</th>
                                    <th style={styles.th}>Ödeme</th>
                                    <th style={{ ...styles.th, textAlign: 'right' }}>Tutar</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recentSales.map(sale => (
                                    <tr key={sale.id}>
                                        <td style={styles.td}>#{sale.receiptNumber}</td>
                                        <td style={{ ...styles.td, color: 'var(--text-muted)' }}>
                                            {sale.saleDate ? new Date(sale.saleDate.toDate()).toLocaleTimeString('tr-TR', {hour: '2-digit', minute:'2-digit'}) : '-'}
                                        </td>
                                        <td style={styles.td}>
                                            <span style={{
                                                ...styles.statusBadge,
                                                background: sale.paymentMethod === 'cash' ? '#dcfce7' : '#e0e7ff',
                                                color: sale.paymentMethod === 'cash' ? '#166534' : '#3730a3'
                                            }}>
                                                {sale.paymentMethod === 'cash' ? 'Nakit' : sale.paymentMethod === 'card' ? 'Kredi Kartı' : 'Karışık'}
                                            </span>
                                        </td>
                                        <td style={{ ...styles.td, textAlign: 'right', fontWeight: '600', color: 'var(--text-primary)' }}>
                                            ₺{(sale.total || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                            <div style={{ fontSize: '40px', marginBottom: '12px' }}>🛒</div>
                            <p>Henüz satış bulunmuyor.</p>
                        </div>
                    )}
                </div>

                <div style={styles.tableContainer}>
                    <h2 style={styles.tableTitle}>Gelen Sevkiyatlar</h2>
                    {recentTransfers.length > 0 ? (
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Tarih</th>
                                    <th style={styles.th}>Durum</th>
                                    <th style={{ ...styles.th, textAlign: 'right' }}>Ürün Çeşidi</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recentTransfers.map(tr => (
                                    <tr key={tr.id}>
                                        <td style={{ ...styles.td, color: 'var(--text-muted)' }}>
                                            {tr.date ? new Date(tr.date.toDate()).toLocaleDateString('tr-TR') : '-'}
                                        </td>
                                        <td style={styles.td}>
                                            <span style={{
                                                ...styles.statusBadge,
                                                background: '#dcfce7',
                                                color: '#166534'
                                            }}>
                                                Tamamlandı
                                            </span>
                                        </td>
                                        <td style={{ ...styles.td, textAlign: 'right', fontWeight: '600', color: 'var(--text-primary)' }}>
                                            {tr.items?.length || 0}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                            <div style={{ fontSize: '40px', marginBottom: '12px' }}>📦</div>
                            <p>Kaydedilmiş sevkiyat bulunmuyor.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
